/** Small pieces the driver screens share: places, payment methods, statuses. */
import { when } from "@taxi/mobile-web/format";
import { BriefcaseIcon, CardIcon, CashIcon } from "@taxi/mobile-web/icons";
import type { ReactNode } from "react";
import type { DriverStatus, PaymentMethod } from "../lib/types";

type PlaceLike = { address: string; name?: string | null };

/** A place as a short title and the rest of the address under it. */
export function placeParts(place: PlaceLike): { title: string; rest: string } {
  if (place.name) return { title: place.name, rest: place.address };
  const comma = place.address.indexOf(",");
  return comma > 0
    ? { title: place.address.slice(0, comma), rest: place.address.slice(comma + 1).trim() }
    : { title: place.address, rest: "" };
}

/** Pickup, stops and destination, top to bottom. */
export function TripRoute({
  pickup,
  stops = [],
  dropoff,
}: {
  pickup: PlaceLike;
  stops?: PlaceLike[];
  dropoff: PlaceLike;
}) {
  const item = (place: PlaceLike, kind: string, key: string) => {
    const { title, rest } = placeParts(place);
    return (
      <li key={key} className={kind}>
        {title}
        {rest && <span>{rest}</span>}
      </li>
    );
  };
  return (
    <ol className="route-list">
      {item(pickup, "pickup", "pickup")}
      {stops.map((stop, i) => item(stop, "stop", `stop-${i}-${stop.address}`))}
      {item(dropoff, "dropoff", "dropoff")}
    </ol>
  );
}

export const PAYMENT_LABELS: Record<PaymentMethod, string> = {
  card: "Card",
  cash: "Cash",
  account: "Account",
};

export function PaymentIcon({ method, size = 18 }: { method: PaymentMethod; size?: number }) {
  if (method === "cash") return <CashIcon size={size} />;
  if (method === "account") return <BriefcaseIcon size={size} />;
  return <CardIcon size={size} />;
}

export function PaymentTag({ method }: { method: PaymentMethod }) {
  return (
    <span className={`payment-tag payment-${method}`}>
      <PaymentIcon method={method} size={15} />
      {PAYMENT_LABELS[method]}
    </span>
  );
}

export const STATUS_LABELS: Record<DriverStatus, string> = {
  off: "Off",
  available: "Available",
  paused: "Paused",
  on_job: "On a job",
};

export function StatusChip({ status, children }: { status: DriverStatus; children?: ReactNode }) {
  return (
    <span className={`driver-status driver-status-${status}`}>
      <i aria-hidden="true" />
      {STATUS_LABELS[status]}
      {children}
    </span>
  );
}

/** "Now", or the day and time. */
export const pickupWhen = (job: { isAsap: boolean; pickupAt: string }, now = new Date()) =>
  job.isAsap ? "Now" : when(new Date(job.pickupAt), now);

/** A UK date ("2026-10-03") as "3 Oct 2026". */
export function shortDate(date: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${date}T12:00:00Z`));
}
